import type { AxiosInstance } from 'axios';

export class NetworkClient {
	constructor(
		private readonly client: AxiosInstance,
		private includeCredentials = true
	) {}

	/**
	 * Sends a GET request to the specified path with the provided options.
	 * @public
	 * @param path {string} - The URL path to send the request to.
	 * @param options {object} - Optional additional options for the request.
	 * @returns {Promise<any>} - A promise that resolves to the response data.
	 */
	public async get(path: string, options = {}) {
		const response = await this.client.get(path, {
			...options,
			withCredentials: this.includeCredentials,
		});

		return response.data;
	}

	/**
	 * Sends a POST request to the specified path with the provided data, headers, and options.
	 * @public
	 * @param path {string} - The URL path to send the request to.
	 * @param data {unknown} - The data to be sent in the request body.
	 * @param headers {object} - Optional headers to be included in the request.
	 * @param options {object} - Optional additional options for the request.
	 * @returns {Promise<any>} - A promise that resolves to the response data.
	 */
	public async post(path: string, data: unknown, headers = {}, options = {}) {
		const response = await this.client.post(path, data, {
			headers: {
				'Content-Type': 'application/json',
				...headers,
			},
			...options,
			withCredentials: this.includeCredentials,
		});

		return response.data;
	}

	/**
	 * Sends a PATCH request to the specified path with the provided data, headers, and options.
	 * @public
	 * @param path {string} - The URL path to send the request to.
	 * @param data {unknown} - The data to be sent in the request body.
	 * @param headers {object} - Optional headers to be included in the request.
	 * @param options {object} - Optional additional options for the request.
	 * @returns {Promise<any>} - A promise that resolves to the response data.
	 */
	public async patch(path: string, data: unknown, headers = {}, options = {}) {
		const response = await this.client.patch(path, data, {
			headers: {
				'Content-Type': 'application/json',
				...headers,
			},
			...options,
			withCredentials: this.includeCredentials,
		});

		return response.data;
	}

	/**
	 * Sends a PUT request to the specified path with the provided data, headers, and options.
	 * @public
	 * @param path {string} - The URL path to send the request to.
	 * @param data {unknown} - The data to be sent in the request body.
	 * @param headers {object} - Optional headers to be included in the request.
	 * @param options {object} - Optional additional options for the request.
	 * @returns {Promise<any>} - A promise that resolves to the response data.
	 */
	public async put(path: string, data: unknown, headers = {}, options = {}) {
		const response = await this.client.put(path, data, {
			headers: {
				'Content-Type': 'application/json',
				...headers,
			},
			...options,
			withCredentials: this.includeCredentials,
		});

		return response.data;
	}

	/**
	 * Sends a DELETE request to the specified path with the provided data, headers, and options.
	 * @public
	 * @param path {string} - The URL path to send the request to.
	 * @param data {unknown} - The data to be sent in the request body.
	 * @param headers {object} - Optional headers to be included in the request.
	 * @param options {object} - Optional additional options for the request.
	 * @returns {Promise<any>} - A promise that resolves to the response data.
	 */
	public async delete(path: string, data?: unknown, headers = {}, options = {}) {
		const response = await this.client.delete(path, {
			headers: {
				'Content-Type': 'application/json',
				...headers,
			},
			data,
			...options,
			withCredentials: this.includeCredentials,
		});

		return response.data;
	}
}
